<template>
  <div ref="dropdown" :class="buttonWarpperClass">
    <!-- Dropdown Toggle Button -->
    <button type="button" :class="buttonClass" :disabled="disabled" @click="toggleDropdown">
      <span :class="buttonTextClass">{{ selectedLabel }}</span>

      <div :class="buttonIconClass">
        <img src="../../assets/images/create-quote-icon.png">
      </div>

      <div :class="buttonDropdownIcon">
        <i class="fa fa-angle-down !text-[24px]" />
      </div>
    </button>

    <!-- Dropdown Menu. floating: rendered on <body> at the field (fixed), so it never pushes the page down and is never
         clipped by a modal / scroll area / carousel; opens upwards when there is no room below -->
    <Teleport to="body" :disabled="!floating">
      <div v-if="isOpen" ref="list" :class="dropdownListClass" :style="floating ? floatStyle : null">
        <ul class="list-inline mb-0 p-0 max-h-60 overflow-y-auto">
          <li v-for="(item, index) in items" :key="index" :class="dropdownListLIClass">
            <a :class="dropdownListAnchorClass" @click="selectItem(item)">
              {{ item[labelKey] }}
            </a>
          </li>
        </ul>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  props: {
    modelValue: {
      type: [String, Number, Object],
      default: null,
    },
    items: {
      type: Array,
      required: true,
    },
    labelKey: {
      type: String,
      default: "label",
    },
    valueKey: {
      type: String,
      default: "value",
    },
    buttonWarpperClass: [String, Array, Object],
    buttonClass: [String, Array, Object],
    buttonIconClass: [String, Array, Object],
    buttonDropdownIcon: [String, Array, Object],
    buttonTextClass: [String, Array, Object],
    buttonText: String,
    dropdownListClass: [String, Array, Object],
    dropdownListLIClass: [String, Array, Object],
    dropdownListAnchorClass: [String, Array, Object],
    disabled: {
      default: false,
    },
    floating: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "select"],
  data() {
    return {
      isOpen: false,
      floatStyle: null,
    };
  },
  computed: {
    selectedLabel() {
      // items can briefly be a non-array (e.g. the store's {} before the API answers)
      const list = Array.isArray(this.items) ? this.items : [];
      const selected = list.find((item) => {
        if (this.valueKey === "object") {
          return JSON.stringify(item) === JSON.stringify(this.modelValue);
        }
        // like a native <select>: 1 and "1" are the same option
        return this.modelValue != null && String(item[this.valueKey]) === String(this.modelValue);
      });
      return selected ? selected[this.labelKey] : this.buttonText;
    },
  },
  watch: {
    isOpen(open) {
      if (open) {
        window.addEventListener("scroll", this.onScroll, true);
        if (this.floating) {
          this.place();
          this.$nextTick(this.place);
          window.addEventListener("resize", this.place);
        }
      } else {
        this.removeFloatListeners();
      }
    },
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    this.removeFloatListeners();
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },
    selectItem(item) {
      const value = this.valueKey === "object" ? item : item[this.valueKey];
      this.$emit("update:modelValue", value);
      this.$emit("select", item);
      this.isOpen = false;
    },
    handleClickOutside(event) {
      // the floating list lives on <body>: a click in it (e.g. its scrollbar) is not "outside"
      if (this.$refs.list?.contains(event.target)) return;
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.isOpen = false;
      }
    },
    place() {
      const field = this.$refs.dropdown?.getBoundingClientRect();
      if (!field) return;
      const listHeight = Math.min(this.$refs.list?.scrollHeight || 240, 248);
      const below = window.innerHeight - field.bottom;
      const up = below < listHeight + 12 && field.top > below;
      this.floatStyle = {
        position: "fixed",
        left: field.left + "px",
        width: field.width + "px",
        top: up ? "auto" : field.bottom + 6 + "px",
        bottom: up ? window.innerHeight - field.top + 6 + "px" : "auto",
        margin: 0,
      };
    },
    // the page / modal scrolled: close (a fixed list jumped around while scrolling, esp. on iOS); scrolling inside the list itself keeps it open
    onScroll(event) {
      if (this.$refs.list?.contains(event.target)) return;
      this.isOpen = false;
    },
    removeFloatListeners() {
      window.removeEventListener("scroll", this.onScroll, true);
      window.removeEventListener("resize", this.place);
    },
  },
};
</script>
